export type PortMegaLink = {
  title: string;
  description: string;
  href: string;
};

export type PortMegaFeatured = {
  title: string;
  href: string;
  imageUrl: string;
};

export type PortMegaMenuConfig = {
  id: "servicios" | "barcos" | "experiencias";
  label: string;
  columns: Array<{
    heading: string;
    links: PortMegaLink[];
  }>;
  featured: {
    heading: string;
    items: PortMegaFeatured[];
  };
  promo: {
    title: string;
    description: string;
    cta: string;
    href: string;
  };
};

const serviceHref = (
  servicesHrefBySlug: Record<string, string>,
  slug: string,
  fallback = "#servicios",
) => servicesHrefBySlug[slug] ?? fallback;

export const buildPortMegaMenus = (
  boatsUrl: string,
  servicesHrefBySlug: Record<string, string> = {},
  portalUrl = "",
): PortMegaMenuConfig[] => {
  const menus: PortMegaMenuConfig[] = [
  {
    id: "servicios",
    label: "Servicios náuticos",
    columns: [
      {
        heading: "Por tipo de servicio",
        links: [
          {
            title: "Mecánica",
            description: "Motores, transmisiones y revisión técnica.",
            href: serviceHref(servicesHrefBySlug, "mecanica"),
          },
          {
            title: "Antifouling",
            description: "Protección del casco y preparación de obra viva.",
            href: serviceHref(servicesHrefBySlug, "antifouling"),
          },
          {
            title: "Electricidad",
            description: "Instalaciones, baterías y diagnosis a bordo.",
            href: serviceHref(servicesHrefBySlug, "electricidad"),
          },
          {
            title: "Limpieza",
            description: "Interior, exterior y puesta a punto.",
            href: serviceHref(servicesHrefBySlug, "limpieza"),
          },
        ],
      },
      {
        heading: "Por necesidad",
        links: [
          {
            title: "Tengo una avería",
            description: "Asistencia rápida cuando algo falla.",
            href: "#averia",
          },
          {
            title: "Necesito un presupuesto",
            description: "Solicita una valoración sin compromiso.",
            href: serviceHref(servicesHrefBySlug, "antifouling"),
          },
          {
            title: "Mantenimiento anual",
            description: "Planifica revisiones y evita sorpresas.",
            href: serviceHref(servicesHrefBySlug, "mantenimiento"),
          },
          {
            title: "ITV e inspección",
            description: "Inspecciones y requisitos oficiales.",
            href: serviceHref(servicesHrefBySlug, "itb"),
          },
        ],
      },
    ],
    featured: {
      heading: "Casos destacados",
      items: [
        {
          title: "Revisión pretemporada",
          href: serviceHref(servicesHrefBySlug, "mantenimiento"),
          imageUrl: "/images/services/mantenimiento.webp",
        },
        {
          title: "Diagnóstico de motor",
          href: serviceHref(servicesHrefBySlug, "mecanica"),
          imageUrl: "/images/services/mecanica.webp",
        },
        {
          title: "Puesta a punto express",
          href: serviceHref(servicesHrefBySlug, "limpieza"),
          imageUrl: "/images/services/limpieza.webp",
        },
      ],
    },
    promo: {
      title: "Tu barco, en las mejores manos",
      description:
        "Servicios náuticos en el puerto para mantenimiento, averías y preparación de temporada.",
      cta: "Ver servicios",
      href: "#servicios",
    },
  },
  {
    id: "barcos",
    label: "Barcos",
    columns: [
      {
        heading: "Por tipo",
        links: [
          {
            title: "Alquiler de barcos",
            description: "Lanchas, veleros y salidas por el día.",
            href: boatsUrl,
          },
          {
            title: "Charter con patrón",
            description: "Navega sin preocuparte del rumbo.",
            href: boatsUrl,
          },
          {
            title: "Vela ligera",
            description: "Opciones ágiles para aprender y disfrutar.",
            href: "#barcos",
          },
          {
            title: "Motos de agua",
            description: "Experiencias rápidas junto a la costa.",
            href: "#barcos",
          },
        ],
      },
      {
        heading: "Por necesidad",
        links: [
          {
            title: "Busco un barco",
            description: "Encuentra la embarcación ideal para hoy.",
            href: boatsUrl,
          },
          {
            title: "Salida en familia",
            description: "Opciones cómodas y seguras para todos.",
            href: boatsUrl,
          },
          {
            title: "Puesta de sol",
            description: "Salidas cortas para disfrutar del Mediterráneo.",
            href: boatsUrl,
          },
          {
            title: "Ya tengo reserva",
            description: "Accede a tu cuenta de alquiler.",
            href: `${boatsUrl.split("?")[0].replace(/\/$/, "")}/account`,
          },
        ],
      },
    ],
    featured: {
      heading: "Casos destacados",
      items: [
        {
          title: "Alquiler por horas",
          href: boatsUrl,
          imageUrl: "/images/ecosystem/port.jpg",
        },
        {
          title: "Día completo en el mar",
          href: boatsUrl,
          imageUrl: "/images/ecosystem/port.jpg",
        },
        {
          title: "Descubrir el catálogo",
          href: boatsUrl,
          imageUrl: "/images/ecosystem/port.jpg",
        },
      ],
    },
    promo: {
      title: "Encuentra tu próxima salida",
      description:
        "Consulta disponibilidad, compara embarcaciones y reserva en la tienda de alquiler del puerto.",
      cta: "Ver barcos",
      href: boatsUrl,
    },
  },
  {
    id: "experiencias",
    label: "Experiencias",
    columns: [
      {
        heading: "Disfrutar del puerto",
        links: [
          {
            title: "Explorar el mapa",
            description: "Servicios, restaurantes y rincones del puerto.",
            href: "#puerto",
          },
          {
            title: "Gastronomía",
            description: "Restaurantes y terrazas junto al mar.",
            href: "/actualidad",
          },
          {
            title: "Eventos",
            description: "Agenda del puerto y próximos encuentros.",
            href: "/actualidad",
          },
          {
            title: "Pasear y descubrir",
            description: "Ideas para una visita completa.",
            href: "#puerto",
          },
        ],
      },
      {
        heading: "Por momento",
        links: [
          {
            title: "Primera visita",
            description: "Orientación rápida para llegar y orientarte.",
            href: "#puerto",
          },
          {
            title: "Comida con vistas",
            description: "Sitios recomendados junto a los amarres.",
            href: "/actualidad",
          },
          {
            title: "Atardecer en el puerto",
            description: "Ambiente, paseo y vida mediterránea.",
            href: "#puerto",
          },
          {
            title: "Actualidad",
            description: "Noticias y novedades de nuestros puertos.",
            href: "/actualidad",
          },
        ],
      },
    ],
    featured: {
      heading: "Casos destacados",
      items: [
        {
          title: "Ruta por el puerto",
          href: "#puerto",
          imageUrl: "/images/ecosystem/port.jpg",
        },
        {
          title: "Restaurantes destacados",
          href: "/actualidad",
          imageUrl: "/images/ecosystem/gestoria.jpg",
        },
        {
          title: "Próximos eventos",
          href: "/actualidad",
          imageUrl: "/images/ecosystem/licencia.jpg",
        },
      ],
    },
    promo: {
      title: "Más que un puerto",
      description:
        "Descubre gastronomía, eventos y experiencias para disfrutar del Maresme.",
      cta: "Explorar el puerto",
      href: "#puerto",
    },
  },
  ];

  if (!portalUrl) return menus;

  const portalHref = portalUrl.replace(/\/$/, "");
  const resolveHref = (href: string) =>
    href.startsWith("#") || href.startsWith("/")
      ? `${portalHref}${href}`
      : href;

  return menus.map((menu) => ({
    ...menu,
    columns: menu.columns.map((column) => ({
      ...column,
      links: column.links.map((link) => ({
        ...link,
        href: resolveHref(link.href),
      })),
    })),
    featured: {
      ...menu.featured,
      items: menu.featured.items.map((item) => ({
        ...item,
        href: resolveHref(item.href),
      })),
    },
    promo: { ...menu.promo, href: resolveHref(menu.promo.href) },
  }));
};
